/* WebComponentHeader component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, h, Listen, Prop } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import HeaderTemplate from './cwc-header.template';
import { AbstractComponent } from '@cewe-designsystem/abstract';
import { HeaderStateClasses } from './states';
import UtilBreakpoints from '@cewe-designsystem/util_breakpoints';

@Component({
  tag: 'cwc-header',
  styleUrl: './styles/cwc-header.scss',
  shadow: false,
})
export class HeaderComponent {
  /**
   * element
   */
  @Element() element: HTMLElement;

  /**
   * version of the header
   */
  @Prop({ attribute: 'v' }) version: number = 1;

  /**
   * handler for breakpoints in css.
   * @private
   */
  private breakPoints: UtilBreakpoints;

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {
    this.breakPoints = new UtilBreakpoints();
  }

  /**
   * Called every time the component is disconnected from the DOM, i.e., it calls be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {}

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {}

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {}

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {}

  /**
   * Called after every render().
   */
  componentDidRender() {}

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It is never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It is never called during the first render().
   */
  componentDidUpdate() {}

  /**
   * render
   */
  render() {
    return <HeaderTemplate controller={this} />;
  }

  @Listen('on:onsite-search:show')
  onSearchStart() {
    this.element.classList.add(HeaderStateClasses.doingSearch);
    this.adjustOnSiteSearchOffset();
  }

  @Listen('on:onsite-search:close')
  onSearchStop() {
    this.element.classList.remove(HeaderStateClasses.doingSearch);
  }

  @Listen('resize', { target: 'window', passive: true })
  adjustOnSiteSearchOffset() {
    const ossOffsetPropertyName = '--cwc-onsite-search-overlay-offset';
    const headerSize = this.element.getBoundingClientRect();
    if (this.version == 2) {
      const searchBox = this.getContent().querySelector<HTMLElement>('cwc-onsite-search');
      const primaryNav = this.getContent().querySelector<HTMLElement>('.primary-nav-area')?.getBoundingClientRect();
      const searchBoxOverlayOffset: number = this.breakPoints.breakpointUp('md') ? primaryNav?.top : primaryNav?.bottom;
      searchBox?.style.setProperty(ossOffsetPropertyName, searchBoxOverlayOffset.toString() + 'px');

      return;
    }

    const preheader = this.getDocument().querySelector<HTMLElement>('preheader');
    const searchDesktop = this.getContent().querySelector<HTMLElement>('.header cwc-onsite-search');
    const searchMobile = this.getContent().querySelector<HTMLElement>('.sg-cw-navigation cwc-onsite-search');

    if (preheader && searchDesktop && this.breakPoints.breakpointUp('md')) {
      // desktop
      const searchBoxSizes = searchDesktop.getBoundingClientRect();
      const preHeaderSize = this.element.classList.contains('stuck') ? 0 : preheader.getBoundingClientRect().height; // if in stuck state preheader is not visible in viewport
      const offset = searchBoxSizes.bottom + preHeaderSize + (searchBoxSizes.top - headerSize.top);
      searchDesktop?.style.setProperty(ossOffsetPropertyName, offset.toString() + 'px');
    }
    if (searchMobile && this.breakPoints.breakpointDown('sm')) {
      //mobile
      searchMobile?.style.setProperty(ossOffsetPropertyName, headerSize.bottom.toString() + 'px');
    }
  }

  @Listen('click', { capture: true, passive: true })
  onNavLinkClick(ev) {
    if (!ev.srcElement.href) {
      return;
    }
    const targetUrl = new URL(ev.srcElement.href);
    const windowUrl = new URL(window.location.href);
    if (targetUrl.pathname == windowUrl.pathname) {
      const mobileNavVisibleCheckbox = document.querySelector('#nav-visible-mobile-toggle') as HTMLInputElement;
      if (mobileNavVisibleCheckbox) {
        mobileNavVisibleCheckbox.checked = false;
      }
    }
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(HeaderComponent.prototype, AbstractComponent.prototype);

export interface HeaderComponent extends AbstractComponent {}
